import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography } from 'antd';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { BackendAPI } from '../../axios';
import { DatabaseOutlined, RiseOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const THEME_COLOR = '#1515b4';

const COLORS = {
  primary: THEME_COLOR,
  secondary: '#3a3ac4',
  accent1: '#5555d4',
  accent2: '#7070e4',
  accent3: '#8b8bf4',
};

const TX_TYPE_COLORS: Record<string, string> = {
  AUTH: COLORS.primary,
  OTP: COLORS.secondary,
  'e-KYC': COLORS.accent1,
};

// API Response Data
const monthlyData = [
  ['2025-09', 19],
  ['2025-01', 91],
  ['2024-12', 127],
  ['2023-11', 35],
  ['2023-06', 10],
  ['2017-09', 784748],
  ['2017-08', 1689517],
];
interface monthDataProps {
  monthData: { month: string; value: number }[];
}
interface weeklyDataProps {
  weekData: { month: string; value: number }[];
}
const transactionTypeCounts = {
  AUTH: 12345,
  OTP: 4321,
  'e-KYC': 9876,
};

const RecentTransactionsDashboard: React.FC = () => {
  // Responsive state
    const [isMobile, setIsMobile] = useState(false);
    const [monthlyData, setMonthlyData] = useState<monthDataProps['monthData']>([]);
    const [otpCount, setOtpCount] = useState(0);
    const [authCount, setAuthCount] = useState(0);
    const [kycCount, setKycCount] = useState(0);
    const [totalCount, setTotalCount] = useState(otpCount + authCount + kycCount);
    const [weeklyData, setWeeklyData] = useState<weeklyDataProps['weekData']>([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Process monthly data
  const chartData = useMemo(() => {
    return monthlyData
      .map((item) => ({
        month: item.month,
        count: item.value,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [monthlyData]);

  // Get date range
  const dateRange = useMemo(() => {
    if (!monthlyData.length) return { start: '', end: '' };
    const months = monthlyData.map((item) => item.month).sort();
    console.log("date: from=> ", months[0]);
    console.log("date: from=> ", months[months.length - 1]);
    return {
      start: months[0],
      end: months[months.length - 1],
    };
  }, [monthlyData]);

  // Process transaction type data for pie chart
  const typeData = useMemo(() => {
  if (totalCount === 0) return [];

  return [
    {
      type: "AUTH",
      count: authCount,
      percentage: ((authCount / totalCount) * 100).toFixed(2),
    },
    {
      type: "OTP",
      count: otpCount,
      percentage: ((otpCount / totalCount) * 100).toFixed(2),
    },
    {
      type: "e-KYC",
      count: kycCount,
      percentage: ((kycCount / totalCount) * 100).toFixed(2),
    },
  ];
}, [authCount, otpCount, kycCount, totalCount]);

  // Calculate totals
  const totalMonthlyTransactions = useMemo(() => {
    return monthlyData.reduce((sum, item) => sum + item.value, 0);
  }, [monthlyData]);

  const totalTypeTransactions = useMemo(() => {
    return Object.values(transactionTypeCounts).reduce((sum, val) => sum + val, 0);
  }, [transactionTypeCounts]);

  // Format large numbers with Indian numbering system
  const formatNumber = (num: number): string => {
    if (num >= 10000000) {
      return `${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `${(num / 100000).toFixed(2)} L`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)} K`;
    }
    return num.toString();
  };
useEffect(() => {
    const header = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    // *********** OTP count fetch *********************
        const fetchOtpCounts = async () => {
          const url = `/txns/otpCount`; 
          try {
            const response = await BackendAPI.get(url, header);
            const result = response.data;
            setOtpCount(result);
          } catch (error) {
            console.error('Error fetching OTP count:', error);
          }
        };
        // *********** AUTH count fetch *********************
        const fetchAuthCounts = async () => {
          const url = `/txns/authCount`;    
          try {
            const response = await BackendAPI.get(url, header);
            const result = response.data;
            setAuthCount(result);
          } catch (error) {
            console.error('Error fetching AUTH count:', error);
          }
        };
        // *********** KYC count fetch *********************
        const fetchKycCounts = async () => {
          const url = `/txns/kycCount`; 
          try {
            const response = await BackendAPI.get(url, header);
            const result = response.data;
            setKycCount(result);
          } catch (error) {
            console.error('Error fetching KYC count:', error);
          }
        };
        // *********** Total count fetch *********************
        const fetchTotalCounts = async () => {
          const url = `/txns/totalCount`;
          try {
            const response = await BackendAPI.get(url, header);
            const result = response.data;
            setTotalCount(result);
          } catch (error) {
            console.error('Error fetching total count:', error);
          }
        };
    // ******************** Monthly graph *********************
    const fetchMonthwiseData = async () => {
      const url = `/txns/weekWiseCount`;
      try {
        const result = await BackendAPI.get(url, header);
        const apiResponse = result.data;

        if (apiResponse) {
          const updatedData = apiResponse.map(([month, value]: [string, number]) => ({
            month,
            value,
          }));
          setMonthlyData((prevData) => {
            const updatedMonthlyData = [...prevData];
            updatedData.forEach((apiItem: any) => {
              const existingItemIndex = updatedMonthlyData.findIndex(
                (item) => item.month === apiItem.month
              );
              if (existingItemIndex !== -1) {
                updatedMonthlyData[existingItemIndex] = apiItem;
              } else {
                updatedMonthlyData.push(apiItem);
              }
            });
            return updatedMonthlyData;
          });
        } else {
          console.error('Invalid numeric value:', result);
        }
      } catch (error) {
        console.error('Error fetching monthly data', error);
      }
    }

    fetchOtpCounts();
    fetchAuthCounts();
    fetchKycCounts();
    fetchTotalCounts();
    fetchMonthwiseData();
  }, []);
  return (
    <div
      style={{
        padding: isMobile ? '10px' : '24px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        minHeight: '100vh', marginTop: '10px', 
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            marginBottom: isMobile ? '16px' : '32px',
            padding: isMobile ? '10px' : '24px',
            background: `linear-gradient(135deg, ${THEME_COLOR} 0%, ${COLORS.secondary} 100%)`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(21, 21, 180, 0.15)',
          }}
        >
          <Title level={isMobile ? 3 : 2} style={{ color: '#fff', margin: 0, marginBottom: '8px' }}>
            Recent Transactions Overview
          </Title>
          {/* <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isMobile ? '12px' : '14px' }}>
            Data Period: {dateRange.start} to {dateRange.end}
          </Text> */}
        </div>
        {/* Summary Statistics - Monthly Data */}
        <Card
          title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '14px' : '16px' }}>Recent Monthly Transactions</span>}
          style={{ marginBottom: isMobile ? '16px' : '24px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: isMobile ? '0px' : '24px'
           }}
           styles={{ body: {padding: isMobile ? '10px' : '24px', } }}
          // styles={{ body: {padding: isMobile ? '0px' : '24px'} }}
        >
          {/* Date Range Info */}
          <div
            style={{
              marginBottom: isMobile ? '12px' : '16px',
              padding: isMobile ? '8px 12px' : '12px 16px',
              background: '#f0f5ff',
              borderRadius: '4px',
              borderLeft: `4px solid ${THEME_COLOR}`,
            }}
          >
            <Text strong style={{ color: THEME_COLOR, fontSize: isMobile ? '12px' : '14px' }}>
              📅 Data Period: {dateRange.start} to {dateRange.end}
            </Text>
            {/* <Text style={{ marginLeft: isMobile ? '8px' : '16px', color: '#666', fontSize: isMobile ? '11px' : '13px' }}>
              ({monthlyData.length} months of data)
            </Text> */}
          </div>
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? '16px' : '24px' }}>
            <Col xs={24} sm={12} lg={8}>
              <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: '8px' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="Total (Last 7 Months)"
                  value={formatNumber(totalMonthlyTransactions)}
                  prefix={<DatabaseOutlined style={{ color: THEME_COLOR }} />}
                  valueStyle={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '20px' : '24px' }}
                  suffix={<Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>{`(${totalMonthlyTransactions.toLocaleString()})`}</Text>}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
                          <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: '8px' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                              <Statistic
                                  title={`Highest Transaction in ${monthlyData.find(
                                              (item) => item.value === Math.max(...monthlyData.map((item) => item.value))
                                          )?.month}`}
                                  value={formatNumber(Math.max(...monthlyData.map((item) => item.value)))}
                                  prefix={<RiseOutlined style={{ color: COLORS.secondary }} />}
                                  valueStyle={{ color: COLORS.secondary, fontWeight: 600, fontSize: isMobile ? '20px' : '24px' }}
                                  suffix={
                                      <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>
                                          {monthlyData.find(
                                              (item) => item.value === Math.max(...monthlyData.map((item) => item.value))
                                          )?.month}
                                      </Text>}
                              />
                          </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: '8px' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="Months Tracked"
                  value={monthlyData.length}
                  valueStyle={{ color: COLORS.accent1, fontWeight: 600, fontSize: isMobile ? '20px' : '24px' }}
                />
              </Card>
            </Col>
          </Row>
{/* Monthly Transactions Chart */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={24} lg={12}>
              <Card
                title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '13px' : '16px' }}>Monthly Trend - Line Chart</span>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="month"
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: `1px solid ${THEME_COLOR}`,
                        borderRadius: '4px',
                      }}
                      formatter={(value: any) => [value.toLocaleString('en-IN'), 'Transactions']}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={THEME_COLOR}
                      strokeWidth={3}
                      dot={{ fill: THEME_COLOR, r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '13px' : '16px' }}>Monthly Volume - Bar Chart</span>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="month"
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: `1px solid ${THEME_COLOR}`,
                        borderRadius: '4px',
                      }}
                      formatter={(value: any) => [value.toLocaleString('en-IN'), 'Transactions']}
                    />
                    <Bar dataKey="count" fill={THEME_COLOR} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </Card>
        {/* Transaction Type Breakdown */}
        <Card
        styles={{
            header: {
                borderLeft: '4px solid rgb(21, 21, 180)',
    borderRadius: '4px',
    background: '#f0f5ff'
            }
        }}
          title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '14px' : '16px' }}>Transaction Type Distribution to Date</span>}
          style={{ marginBottom: isMobile ? '16px' : '24px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
        >
          {/* Type Statistics */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? '16px' : '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: '8px' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="Total"
                  value={formatNumber(totalCount)}
                  prefix={<DatabaseOutlined style={{ color: THEME_COLOR }} />}
                  valueStyle={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '18px' : '24px' }}
                  suffix={<Text type="secondary" style={{ fontSize: isMobile ? '10px' : '14px' }}>{totalCount.toLocaleString('en-IN')}</Text>}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="AUTH"
                  value={formatNumber(authCount)}
                  valueStyle={{ color: TX_TYPE_COLORS.AUTH, fontWeight: 600, fontSize: isMobile ? '18px' : '24px' }}
                  suffix={
                    <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                      {((authCount / totalCount) * 100).toFixed(2)}%
                    </Text>
                  }
                />
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '11px' }}>
                   {authCount.toLocaleString('en-IN')}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="OTP"
                  value={formatNumber(otpCount)}
                  valueStyle={{ color: TX_TYPE_COLORS.OTP, fontWeight: 600, fontSize: isMobile ? '18px' : '24px' }}
                  suffix={
                    <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                      {((otpCount / totalCount) * 100).toFixed(2)}%
                    </Text>
                  }
                />
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '11px' }}>
                   {otpCount.toLocaleString('en-IN')}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <Statistic
                  title="e-KYC"
                  value={formatNumber(kycCount)}
                  valueStyle={{ color: TX_TYPE_COLORS['e-KYC'], fontWeight: 600, fontSize: isMobile ? '18px' : '24px' }}
                  suffix={
                    <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '12px' }}>
                      {((kycCount / totalCount) * 100).toFixed(2)}%
                    </Text>
                  }
                />
                <Text type="secondary" style={{ fontSize: isMobile ? '10px' : '11px' }}>
                  {kycCount.toLocaleString('en-IN')}
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            {/* Pie Chart */}
            <Col xs={24} lg={12}>
              <Card
                title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '13px' : '16px' }}>Type Distribution - Pie Chart</span>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 320 : 350}>
                  <PieChart>
                    <Pie
                      data={typeData}
                      cx="50%"
                      cy={isMobile ? "45%" : "50%"}
                      labelLine={false}
                      label={isMobile ? false : (entry: any) => `${entry.type}: ${entry.percentage}%`}
                      outerRadius={isMobile ? 65 : 100}
                      innerRadius={isMobile ? 30 : 0}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="type"
                    >
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TX_TYPE_COLORS[entry.type]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: `1px solid ${THEME_COLOR}`,
                        borderRadius: '4px',
                        fontSize: isMobile ? '11px' : '12px',
                        padding: isMobile ? '6px 10px' : '8px 12px',
                        maxWidth: isMobile ? '200px' : 'none',
                      }}
                      formatter={(value: any, name: string, props: any) => [
                        `${formatNumber(value)} (${props.payload.percentage}%)`,
                        name,
                      ]}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      wrapperStyle={{
                        paddingTop: '10px',
                        fontSize: isMobile ? '11px' : '12px',
                      }}
                      iconSize={isMobile ? 10 : 14}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Bar Chart */}
            <Col xs={24} lg={12}>
              <Card
                title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '13px' : '16px' }}>Type Volume - Bar Chart</span>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                  <BarChart data={typeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="type" stroke="#666" style={{ fontSize: '12px' }} />
                    <YAxis
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: `1px solid ${THEME_COLOR}`,
                        borderRadius: '4px',
                      }}
                      formatter={(value: any) => [value.toLocaleString(), 'Transactions']}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TX_TYPE_COLORS[entry.type]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Horizontal Bar for Comparison */}
            {/* <Col xs={24}>
              <Card
                title={<span style={{ color: THEME_COLOR, fontWeight: 600, fontSize: isMobile ? '13px' : '16px' }}>Type Comparison - Horizontal View</span>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
              >
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                  <BarChart data={typeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      type="number"
                      stroke="#666"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <YAxis type="category" dataKey="type" stroke="#666" style={{ fontSize: '13px' }} width={80} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: `1px solid ${THEME_COLOR}`,
                        borderRadius: '4px',
                      }}
                      formatter={(value: any, name: string, props: any) => [
                        `${value.toLocaleString()} (${props.payload.percentage}%)`,
                        'Transactions',
                      ]}
                    />
                    <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TX_TYPE_COLORS[entry.type]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col> */}
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default RecentTransactionsDashboard;